/**
 * Reads the asset version actually running in this document, and the published
 * one from a CRA `asset-manifest.json` payload.
 *
 * The running version comes from the live DOM, not from a manifest fetched at
 * boot: inside the wrapper a document can stay alive for days, so a boot-time
 * manifest is not evidence of what this document is running. Both the JS and
 * the CSS entry are compared because a release can change only the stylesheet.
 */

const JS_SELECTOR = 'script[src*="/static/js/main."]';
const CSS_SELECTOR = 'link[rel="stylesheet"][href*="/static/css/main."]';

/** Strip origin/query so a manifest path and a DOM URL compare equal. */
function toComparablePath(rawUrl) {
  if (typeof rawUrl !== "string" || rawUrl === "") {
    return null;
  }
  try {
    return new URL(rawUrl, window.location.origin).pathname;
  } catch {
    return null;
  }
}

/**
 * @returns {{ js: string, css: string | null } | null} `null` when it cannot be
 * determined, in which case callers must do nothing rather than guess.
 */
export function readLoadedAssetVersion() {
  if (typeof document === "undefined") {
    return null;
  }

  const scriptEl = document.querySelector(JS_SELECTOR);
  const js = toComparablePath(scriptEl ? scriptEl.getAttribute("src") : null);
  if (js === null) {
    return null;
  }

  const linkEl = document.querySelector(CSS_SELECTOR);
  return { js, css: toComparablePath(linkEl ? linkEl.getAttribute("href") : null) };
}

/** @returns {{ js: string, css: string | null } | null} */
export function readPublishedAssetVersion(manifest) {
  const files = manifest && typeof manifest === "object" ? manifest.files : null;
  if (files === null || typeof files !== "object") {
    return null;
  }
  const js = toComparablePath(files["main.js"]);
  if (js === null) {
    return null;
  }
  return { js, css: toComparablePath(files["main.css"]) };
}

/** Stable identity for a version pair, used for comparison and de-duplication. */
export function versionKey(version) {
  if (version === null) {
    return null;
  }
  return `${version.js}|${version.css ?? ""}`;
}

/** An unknown version on either side is never reported as an update. */
export function isNewerVersionAvailable(loaded, published) {
  const loadedKey = versionKey(loaded);
  const publishedKey = versionKey(published);
  if (loadedKey === null || publishedKey === null) {
    return false;
  }
  return loadedKey !== publishedKey;
}
